import { PlaycastMessage, PlaycastMessageStateReplication, PlaycastMessageTarget, generators } from '@playcastdotio/Messaging';
import constants from '@playcastdotio/GlobalConfig';
import { channel } from 'diagnostics_channel';
import * as Sentry from '@sentry/react';
import { getElectronStores } from '../../stores/electronStores';
import { getSharedStores } from '../../stores/shared';
import { addReplicationStateChangeCallback, getNotifyMessages } from '@playcastdotio/StateStore';

const PORT = 27123;
const HEARTBEAT_INTERVAL = 20000; // 20 seconds between pings (match server config)
const RECONNECT_TIMEOUT = 3000; // 3 seconds before reconnecting

let wsConnection: WebSocket | null = null;
let isConnected = false;
let messageQueue: PlaycastMessage<PlaycastMessageTarget>[] = [];
let heartbeatInterval: NodeJS.Timeout | null = null;
let reconnectTimeout: NodeJS.Timeout | null = null;
let isReconnecting = false;
let shouldAutoReconnect = true;
let reconnectAttempts = 0;
const maxReconnectAttempts = 100;
const baseReconnectDelay = 1000; // 1 second base delay

// Message handlers
type MessageHandler = (message: PlaycastMessage<PlaycastMessageTarget>) => void;
const messageHandlers = new Map<string, MessageHandler[]>();

const clearReconnectTimeout = () => {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
};

const startHeartbeat = () => {
  stopHeartbeat();

  heartbeatInterval = setInterval(() => {
    if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN) {
      Sentry.addBreadcrumb({
        category: 'node-service',
        message: 'Heartbeat detected invalid websocket',
        level: 'warning',
        data: { readyState: wsConnection?.readyState ?? null },
      });
      stopHeartbeat();
      disconnect();
      if (shouldAutoReconnect) {
        handleReconnect();
      }
      return;
    }

    try {
      const heartbeatMessage = generators.service.ping('experimental');
      wsConnection.send(JSON.stringify(heartbeatMessage));
    } catch (error) {
      console.error('[Node Service Client] Failed to send heartbeat:', error);
      Sentry.addBreadcrumb({
        category: 'node-service',
        message: 'Heartbeat send failed',
        level: 'error',
        data: { error: error?.toString() || 'Unknown error' },
      });
      stopHeartbeat();
      disconnect();
      if (shouldAutoReconnect) {
        handleReconnect();
      }
    }
  }, HEARTBEAT_INTERVAL);
};

const stopHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
};

const handleReconnect = () => {
  if (!shouldAutoReconnect) {
    console.log('[Node Service Client] Auto-reconnect disabled');
    return;
  }

  // Check if we've exceeded max attempts
  if (reconnectAttempts >= maxReconnectAttempts) {
    console.error(`[Node Service Client] Max reconnection attempts (${maxReconnectAttempts}) reached. Stopping reconnection.`);
    shouldAutoReconnect = false;
    return;
  }

  // Prevent multiple simultaneous reconnection attempts
  if (isReconnecting) {
    console.log('[Node Service Client] Reconnection already in progress');
    return;
  }

  isReconnecting = true;
  reconnectAttempts++;

  // Calculate exponential backoff delay
  const delay = Math.min(baseReconnectDelay * Math.pow(2, reconnectAttempts - 1), 30000); // Max 30 seconds

  console.log(`[Node Service Client] Attempting to reconnect (attempt ${reconnectAttempts}/${maxReconnectAttempts}) in ${delay}ms...`);

  // Clean up existing connection if it exists
  disconnect();

  // Wait before attempting to reconnect
  clearReconnectTimeout();
  reconnectTimeout = setTimeout(() => {
    try {
      // Reset wsConnection to null before attempting to reconnect
      wsConnection = null;
      connect();
    } catch (error) {
      console.error('[Node Service Client] Reconnection attempt failed:', error);
      // Don't call handleReconnect recursively here, let the connection failure handle it
    } finally {
      isReconnecting = false;
    }
  }, delay);
};

const processQueue = () => {
  while (messageQueue.length > 0 && isConnected && wsConnection?.readyState === WebSocket.OPEN) {
    const message = messageQueue.shift();
    if (message) {
      try {
        wsConnection.send(JSON.stringify(message));
        console.log('[Node Service Client] Sent queued message:', message.header.action);
      } catch (error) {
        console.error('[Node Service Client] Failed to send queued message:', error);
        messageQueue.unshift(message); // Put the message back at the start of the queue
        break;
      }
    }
  }
};

const electronState = getElectronStores();
const sharedStateStores = getSharedStores();
const handleIncomingMessage = (message: PlaycastMessage<PlaycastMessageTarget>) => {
  console.log('[Node Service Client] Received message:', message.header.action, 'target:', message.header.target);

  if (message.header.target === 'state' && message.header.action === 'replication') {
    console.log('[Node Service Client] Received replication message:', message);
    const stateMessage = message as PlaycastMessage<PlaycastMessageStateReplication<any>>;
    const channel = stateMessage.body.message.channel;
    if (electronState[channel]) {
      electronState[channel].updateState(stateMessage.body.message.state as never);
    } else if (sharedStateStores[channel]) {
      sharedStateStores[channel].updateState(stateMessage.body.message.state as never);
    } else {
      console.error('[Node Service Client] Invalid channel:', channel);
    }
    return;
  }

  // Call registered handlers for this action
  const handlers = messageHandlers.get(message.header.action);
  if (handlers && handlers.length > 0) {
    handlers.forEach((handler) => {
      try {
        handler(message);
      } catch (error) {
        console.error(`[Node Service Client] Error in message handler for ${message.header.action}:`, error);
      }
    });
  } else {
    console.log(`[Node Service Client] No handlers registered for action: ${message.header.action}`);
  }
};

export const connect = () => {
  if (wsConnection?.readyState === WebSocket.OPEN || wsConnection?.readyState === WebSocket.CONNECTING) {
    console.warn('[Node Service Client] Connection already exists');
    return;
  }

  const wsUrl = `ws://localhost:${PORT}`; // Node service WebSocket server
  console.log('[Node Service Client] Connecting to:', wsUrl);

  try {
    wsConnection = new WebSocket(wsUrl);

    wsConnection.onopen = () => {
      console.log('[Node Service Client] Connected to node service');

      // Reset reconnection attempts on successful connection
      reconnectAttempts = 0;

      isConnected = true;
      startHeartbeat();
      processQueue();
      const replicationMessages = getNotifyMessages(['nodeService']);
      replicationMessages.nodeService.forEach((message) => {
        console.log('[Node Service Client] Sending replication message:', message.header.action);
        sendMessage(message);
      });
    };

    wsConnection.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as PlaycastMessage<PlaycastMessageTarget>;
        handleIncomingMessage(message);
      } catch (error) {
        console.error('[Node Service Client] Failed to parse message:', error);
      }
    };

    wsConnection.onclose = (event) => {
      console.log('[Node Service Client] Connection closed:', event.code, event.reason);
      isConnected = false;
      stopHeartbeat();

      // Always attempt reconnect if auto-reconnect is enabled
      if (shouldAutoReconnect && !isReconnecting) {
        console.log('[Node Service Client] Connection lost, attempting to reconnect...');
        handleReconnect();
      }
    };

    wsConnection.onerror = (error) => {
      console.error('[Node Service Client] WebSocket error:', error);
      // Don't call handleReconnect here - let onclose handle it
    };
  } catch (error) {
    console.error('[Node Service Client] Failed to connect:', error);
    if (shouldAutoReconnect) {
      handleReconnect();
    }
  }
};

export const disconnect = (disableAutoReconnect = false) => {
  console.log('[Node Service Client] Disconnecting...', disableAutoReconnect ? '(auto-reconnect disabled)' : '');

  if (disableAutoReconnect) {
    shouldAutoReconnect = false;
  }

  stopHeartbeat();
  clearReconnectTimeout();
  isConnected = false;
  messageQueue = [];

  if (wsConnection) {
    // Store reference and null the connection before closing
    const conn = wsConnection;
    wsConnection = null;

    try {
      conn.close(1000, 'Client disconnect');
    } catch (error) {
      console.error('[Node Service Client] Error while closing connection:', error);
    }
  }
};

export const sendMessage = (message: PlaycastMessage<PlaycastMessageTarget>) => {
  if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN || !isConnected) {
    console.warn('[Node Service Client] Connection not ready, queueing message:', message.header.action);
    messageQueue.push(message);
    return false;
  }

  try {
    wsConnection.send(JSON.stringify(message));
    console.log('[Node Service Client] Sent message:', message.header.action);
    return true;
  } catch (error) {
    console.error('[Node Service Client] Failed to send message:', error);
    messageQueue.push(message);
    return false;
  }
};

// Handler registration system
export const onMessage = (action: string, handler: MessageHandler) => {
  if (!messageHandlers.has(action)) {
    messageHandlers.set(action, []);
  }
  messageHandlers.get(action)?.push(handler);

  console.log(`[Node Service Client] Registered handler for action: ${action}`);
};

export const offMessage = (action: string, handler: MessageHandler) => {
  const handlers = messageHandlers.get(action);
  if (handlers) {
    const index = handlers.indexOf(handler);
    if (index > -1) {
      handlers.splice(index, 1);
      console.log(`[Node Service Client] Unregistered handler for action: ${action}`);
    }
  }
};

export const clearMessageHandlers = (action?: string) => {
  if (action) {
    messageHandlers.delete(action);
    console.log(`[Node Service Client] Cleared handlers for action: ${action}`);
  } else {
    messageHandlers.clear();
    console.log('[Node Service Client] Cleared all message handlers');
  }
};

// Utility functions
export const isNodeServiceConnected = (): boolean => {
  return wsConnection?.readyState === WebSocket.OPEN && isConnected;
};

export const getConnectionStatus = () => {
  return {
    connected: isNodeServiceConnected(),
    reconnecting: isReconnecting,
    queueSize: messageQueue.length,
    autoReconnect: shouldAutoReconnect,
    reconnectAttempts,
    maxReconnectAttempts,
    connectionState: wsConnection?.readyState ?? -1,
  };
};

export const setAutoReconnect = (enabled: boolean) => {
  shouldAutoReconnect = enabled;
  console.log(`[Node Service Client] Auto-reconnect ${enabled ? 'enabled' : 'disabled'}`);
};

// Additional utility functions for controlling reconnection behavior
export const enableAutoReconnect = () => {
  shouldAutoReconnect = true;
  reconnectAttempts = 0; // Reset attempts when re-enabling
  console.log('[Node Service Client] Auto-reconnect enabled');
};

export const disableAutoReconnect = () => {
  shouldAutoReconnect = false;
  clearReconnectTimeout();
  console.log('[Node Service Client] Auto-reconnect disabled');
};

export const forceReconnect = () => {
  console.log('[Node Service Client] Forcing reconnection...');
  reconnectAttempts = 0; // Reset attempts for forced reconnect
  shouldAutoReconnect = true;
  handleReconnect();
};

addReplicationStateChangeCallback('nodeService', (message) => {
  console.log('[Node Service Client] Received replication message:', message);
  sendMessage(message);
});
